// Stacked-area chart of the §6.3.2 lifecycle distribution over the last
// 30 UTC days. NOT a pie chart — per PRD §6.3.2 "time dimension matters",
// a single-day snapshot hides the drift signal.
//
// Visual intent: positive stages (ENGAGED, ACTIVE, NEW) anchor the bottom
// of the stack so they remain visible even when AT_RISK / CHURNED bands
// dominate the top — same intent the legend is sorted by. Stage colors
// pull from `entities/member/lib/stage-colors.ts` so the badges, the
// legend, and the chart series share one canonical palette.
//
// Reuses the entity-level color map and is otherwise self-contained:
// pages compose this widget directly from the parsed
// `LifecycleResponse.trend` payload.

import type { ReactElement } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";

import type {
  LifecycleDailyPoint,
  MemberLifecycleStage,
} from "@community-analytics/shared";

import { STAGE_STYLES } from "@entities/member";
import { cn } from "@shared/lib";

import {
  LIFECYCLE_STACK_ORDER,
  LIFECYCLE_TREND_STRINGS,
  STAGE_LABELS,
} from "../lib/strings";

export interface LifecycleTrendChartProps {
  trend: ReadonlyArray<LifecycleDailyPoint>;
  className?: string;
}

const HEIGHT = 240;

interface ChartRow {
  date: string;
  label: string;
  total: number;
  NEW: number;
  ACTIVE: number;
  ENGAGED: number;
  DORMANT: number;
  AT_RISK: number;
  CHURNED: number;
}

export function LifecycleTrendChart({
  trend,
  className,
}: LifecycleTrendChartProps) {
  const data: ChartRow[] = trend.map((point) => ({
    date: point.date,
    label: dayLabel(point.date),
    total: point.totalMembers,
    NEW: point.counts.NEW,
    ACTIVE: point.counts.ACTIVE,
    ENGAGED: point.counts.ENGAGED,
    DORMANT: point.counts.DORMANT,
    AT_RISK: point.counts.AT_RISK,
    CHURNED: point.counts.CHURNED,
  }));

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border bg-background p-3",
        className,
      )}
    >
      <ResponsiveContainer width="100%" height={HEIGHT}>
        <AreaChart
          data={data}
          margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="2 4"
            stroke="hsl(var(--border))"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={32}
          />
          <Tooltip
            cursor={{
              stroke: "hsl(var(--border))",
              strokeDasharray: "2 2",
            }}
            content={<LifecycleTooltip />}
          />
          {LIFECYCLE_STACK_ORDER.map((stage) => (
            <Area
              key={stage}
              type="monotone"
              dataKey={stage}
              stackId="lifecycle"
              stroke={STAGE_STYLES[stage].hex}
              fill={STAGE_STYLES[stage].hex}
              fillOpacity={0.6}
              isAnimationActive={false}
              name={STAGE_LABELS[stage]}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <Legend />
    </div>
  );
}

function Legend() {
  return (
    <ul
      aria-label="Stage legend"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
    >
      {LIFECYCLE_STACK_ORDER.map((stage) => (
        <li key={stage} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-2 w-3 rounded-sm"
            style={{ background: STAGE_STYLES[stage].hex, opacity: 0.6 }}
          />
          <span>{STAGE_LABELS[stage]}</span>
        </li>
      ))}
    </ul>
  );
}

function LifecycleTooltip(
  props: TooltipProps<number, string>,
): ReactElement | null {
  const { active, payload } = props;
  if (!active || !payload || payload.length === 0) return null;
  const first = payload[0]?.payload as ChartRow | undefined;
  if (!first) return null;
  return (
    <div
      className="rounded-md border bg-background p-2 text-xs shadow-sm"
      style={{ minWidth: 160 }}
    >
      <div className="pb-1 font-medium text-foreground">
        {LIFECYCLE_TREND_STRINGS.tooltipDateLabel(first.date)}
      </div>
      <ul className="flex flex-col gap-0.5">
        {LIFECYCLE_STACK_ORDER.map((stage) => (
          <li key={stage} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-sm"
                style={{
                  background: STAGE_STYLES[stage].hex,
                  opacity: 0.8,
                }}
              />
              <span className="text-muted-foreground">
                {STAGE_LABELS[stage]}
              </span>
            </span>
            <span className="tabular-nums text-foreground">
              {first[stage as MemberLifecycleStage]}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-1 border-t pt-1 text-muted-foreground">
        {LIFECYCLE_TREND_STRINGS.tooltipTotalLabel(first.total)}
      </div>
    </div>
  );
}

function dayLabel(yyyyMmDd: string): string {
  // Two-letter weekday tick keeps the 30-day axis legible on a Mini App
  // viewport (~360px wide). Using UTC matches the API's day bucketing so
  // the label aligns with the date.
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" });
}
